import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import axios from "axios";
import "./EventsAndNewsPage.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

export default function EventsAndNewsPage() {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [errorNews, setErrorNews] = useState(null);
  const [errorEvents, setErrorEvents] = useState(null);

  // =======================================
  // FETCH NEWS
  // =======================================
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/news`);
        setNews(response.data);
      } catch (err) {
        console.error("Fetch News Error:", err);
        setErrorNews("Unable to load news.");
      } finally {
        setLoadingNews(false);
      }
    };

    fetchNews();
  }, []);

  // =======================================
  // FETCH EVENTS
  // =======================================
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/events`);
        setEvents(response.data);
      } catch (err) {
        console.error("Fetch Events Error:", err);
        setErrorEvents("Unable to load events.");
      } finally {
        setLoadingEvents(false);
      }
    };

    fetchEvents();
  }, []);

  // Format Date Helper
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <section className="events-news-page">
      <div className="container">
        {/* Page Header */}
        <motion.div
          className="page-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1>News & Events</h1>
          <p>
            Stay updated with the latest announcements, alumni achievements,
            and upcoming events.
          </p>
        </motion.div>

        {/* ================= NEWS SECTION ================= */}
        <motion.div
          className="news-section"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.15 } },
          }}
        >
          {loadingNews && <p className="loading-text">Loading news...</p>}
          {errorNews && <p className="error-text">{errorNews}</p>}

          {!loadingNews && !errorNews && news.length === 0 && (
            <p className="loading-text">No news available.</p>
          )}

          {!loadingNews &&
            !errorNews &&
            news.map((item, index) => (
              <motion.article
                key={item._id}
                className="news-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                {item.image && (
                  <img
                    src={item.image}
                    alt={item.title}
                    className="news-image"
                  />
                )}

                <div className="news-meta">
                  <span className="news-date">
                    {formatDate(item.createdAt)}
                  </span>
                  <span className="news-category">{item.category}</span>
                </div>

                <h2>{item.title}</h2>
                <p>{item.description}</p>

               
              </motion.article>
            ))}
        </motion.div>

        {/* ================= EVENTS SECTION ================= */}
        <motion.div
          className="events-section"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Events</h2>

          {loadingEvents && <p className="loading-text">Loading events...</p>}
          {errorEvents && <p className="error-text">{errorEvents}</p>}

          {!loadingEvents && !errorEvents && events.length === 0 && (
            <p className="loading-text">No events available.</p>
          )}

          <div className="events-grid">
            {!loadingEvents &&
              !errorEvents &&
              events.map((event, index) => (
                <motion.article
                  key={event._id}
                  className={`event-card ${event.type}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                >
                  {/* Event Image */}
                  {event.image && (
                    <div className="event-image-wrapper">
                      <img
                        src={event.image}
                        alt={event.title}
                        className="event-image"
                      />
                    </div>
                  )}

                  <div className="event-content">
                    <div className="event-date">
                      {formatDate(event.date)}
                    </div>

                    <h3>{event.title}</h3>
                    <span className="event-venue">{event.venue}</span>
                    <p>{event.description}</p>

                    {event.type === "upcoming" && (
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.97 }}
                        className="rsvp-btn"
                      >
                        RSVP
                      </motion.button>
                    )}
                  </div>
                </motion.article>
              ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}